/**
 * Peer-watch triplet logger for `stamp pr listen` (AGT-430).
 *
 * Appends a newline-delimited JSON record
 * `{ ts, repo, pr_url, rules_hash, event_payload, decision }`
 * to `~/.stamp/peer-watch.log` after each triage call (AC #6).
 *
 * Best-effort: a log-write failure logs `✗` to stderr but never aborts the
 * listener loop (crash-safety requirement, AC #2).
 *
 * Injection seam: `_appendForTest` replaces real `appendFileSync` in tests.
 */

import { appendFileSync, mkdirSync, renameSync, statSync } from "node:fs";
import { dirname } from "node:path";
import type { TriageDecision } from "./peerTriage.js";
import { peerWatchLogPath } from "./paths.js";

// ─── Rotation policy ──────────────────────────────────────────────────

/**
 * Rotate `peer-watch.log` once it reaches this many bytes. Keeps the
 * operator log bounded so a stuck/hot listener loop can't exhaust disk
 * (a 7.9 GB file was observed in the wild — OpenThinkAi/stamp-cli#47).
 */
export const PEER_WATCH_LOG_MAX_BYTES = 50 * 1024 * 1024; // 50 MB

/** Number of rotated archives to keep: `peer-watch.log.1` … `.KEEP`. */
export const PEER_WATCH_LOG_KEEP = 3;

/**
 * Size-based rotation. If `logPath` is at/over `maxBytes`, shift the
 * archives down (`.2`→`.3`, `.1`→`.2`) and rename the live log to `.1`,
 * dropping the oldest. Best-effort: any fs error is swallowed so the
 * listener loop never crashes from rotation (matches the append contract).
 */
function rotateIfNeeded(logPath: string, maxBytes: number): void {
  let size: number;
  try {
    size = statSync(logPath).size;
  } catch {
    return; // no log yet (or unstattable) — nothing to rotate
  }
  if (size < maxBytes) return;

  // Shift archives down so `.1` is free, dropping the oldest via overwrite.
  for (let i = PEER_WATCH_LOG_KEEP - 1; i >= 1; i--) {
    try {
      renameSync(`${logPath}.${i}`, `${logPath}.${i + 1}`);
    } catch {
      /* missing source archive is fine */
    }
  }
  try {
    renameSync(logPath, `${logPath}.1`);
  } catch {
    /* best-effort: leave the live log in place if the rename fails */
  }
}

// ─── Types ────────────────────────────────────────────────────────────

export interface TripletRecord {
  /** ISO-8601 timestamp. */
  ts: string;
  /** `owner/repo` string from the event payload. */
  repo: string;
  /** Full GitHub PR URL. */
  pr_url: string;
  /** SHA-256 hex digest of the peer-watch.md bytes used for this call. */
  rules_hash: string;
  /** The raw event payload (untrusted but logged as-is for replay). */
  event_payload: Record<string, unknown>;
  /** The triage decision that was returned. */
  decision: TriageDecision;
  /**
   * Optional event-kind tag (AGT-431). "re-review" distinguishes a
   * `re-review-requested` triage from a fresh `pr-opened` triage in the
   * triplet log. Absent for pr-opened events (for backwards compatibility).
   */
  kind?: string;
  /**
   * Optional override reason (AGT-432). Present when the listener overrides
   * the triage decision — e.g. "daily cap hit" when the cost cap is reached.
   */
  reason?: string;
}

export interface AppendTripletInput extends TripletRecord {
  /**
   * Test-only injection seam: replace real `appendFileSync`.
   * Receives (path, line) where `line` is the NDJSON string including trailing newline.
   */
  _appendForTest?: (path: string, line: string) => void;
  /** Test-only: override the log path (default: `peerWatchLogPath()`). */
  _logPathForTest?: string;
  /** Test-only: override the rotation threshold (default: `PEER_WATCH_LOG_MAX_BYTES`). */
  _maxBytesForTest?: number;
}

// ─── Append ──────────────────────────────────────────────────────────

/**
 * Append one NDJSON record to `~/.stamp/peer-watch.log`.
 *
 * Creates the parent directory if needed. Silently swallows write errors
 * (logged to stderr with `✗` prefix) so the listener loop never crashes
 * from a log failure.
 */
export function appendTriplet(input: AppendTripletInput): void {
  const record: TripletRecord = {
    ts: input.ts,
    repo: input.repo,
    pr_url: input.pr_url,
    rules_hash: input.rules_hash,
    event_payload: input.event_payload,
    decision: input.decision,
    ...(input.kind !== undefined ? { kind: input.kind } : {}),
    ...(input.reason !== undefined ? { reason: input.reason } : {}),
  };

  const line = JSON.stringify(record) + "\n";
  const logPath = input._logPathForTest ?? peerWatchLogPath();

  try {
    if (input._appendForTest) {
      input._appendForTest(logPath, line);
    } else {
      // Ensure the parent dir exists before writing.
      mkdirSync(dirname(logPath), { recursive: true });
      // Bound total size before appending (OpenThinkAi/stamp-cli#47).
      rotateIfNeeded(logPath, input._maxBytesForTest ?? PEER_WATCH_LOG_MAX_BYTES);
      appendFileSync(logPath, line, "utf8");
    }
  } catch (err) {
    process.stderr.write(
      `✗ peer-watch.log write failed: ${err instanceof Error ? err.message : String(err)}\n`,
    );
  }
}
